<div class="questionnaire-container">
  @if (showReview()) {
    <!-- Review Page -->
    <div class="questionnaire-page review-page">
      <div class="page-content">
        <div class="review-header">
          @if (formattedAssessmentType()) {
            <span class="assessment-type-badge">
              <mat-icon class="assessment-type-icon">assessment</mat-icon>
              {{ formattedAssessmentType() }}
            </span>
          }
          <h2 class="review-title">{{ 'questionnaire.review_title' | transloco }}</h2>
          <p class="review-description">
            {{
              'questionnaire.review_description'
                | transloco
                  : {
                      questions: visibleQuestions().length,
                      sections: reviewSectionsSummary().length,
                    }
            }}
          </p>
        </div>

        <div class="review-summary">
          <!-- Pre-questionnaire recap (phase 0). Answered in the create
               wizard and read-only here: the rest of the questionnaire's
               AI suggestions are grounded on this text, so an edit made
               after the fact would leave them stale. -->
          @if (!loading() && preQuestionnaireSummary().length > 0) {
            <div class="review-section-card review-section-card--readonly">
              <div class="review-section-header">
                <div class="review-section-header-row">
                  <div class="review-section-badge">
                    <span class="phase-badge">{{
                      'questionnaire.pre_questionnaire_badge' | transloco
                    }}</span>
                  </div>
                  <div class="review-section-meta">
                    <span class="review-section-readonly-note">
                      <mat-icon class="review-section-readonly-icon">lock</mat-icon>
                      {{ 'questionnaire.pre_questionnaire_readonly' | transloco }}
                    </span>
                  </div>
                </div>
                <p class="review-section-description">
                  {{ 'questionnaire.pre_questionnaire_description' | transloco }}
                </p>
              </div>
              <div class="review-section-questions">
                @for (item of preQuestionnaireSummary(); track item.questionId) {
                  <div class="review-item">
                    <div class="review-question">{{ item.question }}</div>
                    <div
                      class="review-answer"
                      [class.review-answer-textarea]="item.type === 'textarea'"
                    >
                      {{ item.answer }}
                    </div>
                  </div>
                }
              </div>
            </div>
          }

          @if (loading()) {
            @for (card of [0, 1, 2]; track card) {
              <div class="review-section-card">
                <div class="review-section-header">
                  <div class="review-section-header-row">
                    <div appSkeleton style="width: 140px; height: 20px; border-radius: 10px"></div>
                    <div appSkeleton style="width: 80px; height: 16px; border-radius: 8px"></div>
                  </div>
                </div>
                <div class="review-section-questions">
                  @for (row of [0, 1, 2, 3]; track row) {
                    <div class="review-item" style="pointer-events: none">
                      <div
                        appSkeleton
                        style="width: 60%; height: 14px; margin-bottom: 6px; border-radius: 6px"
                      ></div>
                      <div appSkeleton style="width: 35%; height: 13px; border-radius: 6px"></div>
                    </div>
                  }
                </div>
              </div>
            }
          } @else {
            @for (section of reviewSectionsSummary(); track section.section || section.phase) {
              <div class="review-section-card">
                <div class="review-section-header">
                  <div class="review-section-header-row">
                    <div class="review-section-badge">
                      <span class="phase-badge">{{
                        'questionnaire.phase' | transloco: { phase: section.phase }
                      }}</span>
                      @if (section.section) {
                        <span class="phase-separator">•</span>
                        <span class="section-badge">{{ section.section }}</span>
                      }
                    </div>
                    <div class="review-section-meta">
                      <span class="review-section-count">{{
                        'questionnaire.questions_count'
                          | transloco: { count: section.questions.length }
                      }}</span>
                      <mat-icon class="review-section-check">check_circle</mat-icon>
                    </div>
                  </div>
                  @if (section.sectionDescription) {
                    <p class="review-section-description">{{ section.sectionDescription }}</p>
                  }
                </div>
                <div class="review-section-questions">
                  @for (item of section.questions; track item.questionId) {
                    <div class="review-item">
                      <div class="review-question">
                        <span class="question-number">{{
                          'questionnaire.question_prefix'
                            | transloco: { number: getQuestionNumber(item.questionId) }
                        }}</span>
                        {{ item.question }}
                      </div>
                      <div
                        class="review-answer"
                        [class.review-answer-textarea]="item.type === 'textarea'"
                      >
                        {{ item.answer }}
                      </div>
                    </div>
                  }
                </div>
              </div>
            }
          }
          <!-- end @else (not loading) -->
        </div>
      </div>
    </div>

    <!-- Projected content slot: context docs, etc. injected by parent on review page -->
    <ng-content select="[reviewSlot]"></ng-content>

    <!-- Sticky Review Actions Footer -->
    <div class="review-actions-footer">
      <div class="review-actions-inner">
        <button
          class="review-button secondary"
          (click)="goBackToEdit()"
          [disabled]="areReviewButtonsDisabled()"
          type="button"
        >
          <app-action-glyph name="edit-answers" />
          <span>{{ 'questionnaire.edit_answers' | transloco }}</span>
        </button>
        <button
          class="review-button primary"
          (click)="confirmReview($event)"
          [disabled]="areReviewButtonsDisabled()"
          type="button"
        >
          <span>{{ generateButtonText() }}</span>
          <app-action-glyph name="synthesis" />
        </button>
      </div>
    </div>
  } @else if (currentPageData()) {
    <!-- Current Page Content -->
    <div class="questionnaire-page">
      <div class="page-content">
        @if (
          recommendationOutcome() ||
          isSuggestionsGenerating() ||
          recommendationContextSources().length ||
          canRegenerateSuggestions()
        ) {
          <section
            class="questionnaire-ai-banner"
            [attr.data-variant]="recommendationVariant()"
            [attr.aria-label]="'questionnaire.recommendation_status_title' | transloco"
          >
            @let bannerToggleLabel =
              (recommendationDetailsExpanded()
                ? 'sidebar.collapse_section_aria'
                : 'sidebar.expand_section_aria'
              ) | transloco: { name: ('questionnaire.recommendation_status_title' | transloco) };
            <div class="questionnaire-ai-banner-header">
              <div class="questionnaire-ai-banner-title">
                <app-action-glyph name="synthesis" />
                <span>{{ 'questionnaire.recommendation_status_title' | transloco }}</span>
              </div>
              <app-button
                type="secondary"
                variant="ghost"
                size="small"
                [iconOnly]="true"
                [ariaExpanded]="recommendationDetailsExpanded()"
                ariaControls="recommendation-details"
                [ariaLabel]="bannerToggleLabel"
                [tooltip]="bannerToggleLabel"
                (clicked)="recommendationDetailsExpanded.set(!recommendationDetailsExpanded())"
              >
                <mat-icon aria-hidden="true">{{
                  recommendationDetailsExpanded() ? 'expand_less' : 'expand_more'
                }}</mat-icon>
              </app-button>
            </div>
            <!-- Recommendation loading indicator. Shared
             `<app-ai-generating-status>` (orb + shimmer) — same cue as
             chat pending turns and the create wizard's pre-questionnaire
             step. Suppressed during a re-run: `recommendation_status` is a
             single per-record field, so it says "pending" even for a
             one-question re-suggest. The affected cards carry their own
             waiting row (scoped by `requested_question_ids`); a page-level
             spinner on top of that would claim the untouched suggestions
             are reloading too. -->
            @if (isSuggestionsGenerating() && !isRegenerating()) {
              <app-ai-generating-status appearance="inline">
                {{ 'questionnaire.recommendation_loading' | transloco }}
              </app-ai-generating-status>
            }

            <div class="recommendation-outcome" role="status" aria-live="polite" aria-atomic="true">
              @if (recommendationOutcome(); as outcome) {
                <p>{{ outcome | transloco }}</p>
              } @else if (isRegenerating()) {
                <p>{{ 'questionnaire.recommendation_regenerating' | transloco }}</p>
              }
            </div>
            <div
              class="questionnaire-ai-banner-details"
              id="recommendation-details"
              [hidden]="!recommendationDetailsExpanded()"
            >
              <div class="recommendation-outcome">
                @if (
                  !recommendationIssue() &&
                  (recommendationStatus() === 'completed' || isRegenerating())
                ) {
                  <p>{{ 'questionnaire.recommendation_context_hint' | transloco }}</p>
                }
              </div>

              <!-- What the suggestions were grounded on + the re-run actions.
             The "based on" line answers the scoping question once per
             page rather than repeating it on all 30 cards. -->
              @if (recommendationContextSources().length || canRegenerateSuggestions()) {
                <div class="ai-suggestion-meta">
                  @if (recommendationContextSources().length) {
                    <span class="ai-suggestion-sources">
                      <mat-icon class="ai-suggestion-sources-icon">info</mat-icon>
                      {{
                        'questionnaire.recommendation_sources'
                          | transloco: { sources: recommendationContextSources().join(', ') }
                      }}
                    </span>
                  }
                  @if (canRegenerateSuggestions()) {
                    <span class="ai-suggestion-actions">
                      <!-- Default bulk run: unanswered only. Never touches an
                     answer the user gave. -->
                      <button
                        type="button"
                        class="ai-suggestion-apply ai-suggestion-regenerate"
                        (click)="requestRegenerate('unanswered')"
                        [disabled]="disabled() || isRecommendationBusy()"
                      >
                        <mat-icon class="ai-suggestion-regenerate-icon">refresh</mat-icon>
                        {{ 'questionnaire.recommendation_regenerate' | transloco }}
                      </button>
                      <!-- Start over: the only mode that replaces suggestions
                     for questions already answered, and the only one that
                     loses cross-question grounding. Demoted, and the tooltip
                     spells out the trade. -->
                      <button
                        type="button"
                        class="ai-suggestion-apply ai-suggestion-apply--demoted"
                        (click)="requestRegenerate('all')"
                        [disabled]="disabled() || isRecommendationBusy()"
                        matTooltipPosition="above"
                        [matTooltip]="
                          'questionnaire.recommendation_regenerate_all_hint' | transloco
                        "
                      >
                        {{ 'questionnaire.recommendation_regenerate_all' | transloco }}
                      </button>
                    </span>
                  }
                </div>
              }
            </div>
          </section>
        }

        <!-- Phase Indicator -->
        <div class="phase-indicator">
          <span class="phase-badge">{{
            'questionnaire.phase' | transloco: { phase: currentPageData()!.phase }
          }}</span>
          @if (currentPageData()!.section) {
            <span class="phase-separator">•</span>
            <span class="section-badge">{{ currentPageData()!.section }}</span>
          }
        </div>

        @if (currentPageData()!.section) {
          <div class="questionnaire-section-header">
            <h2 class="questionnaire-section-title">{{ currentPageData()!.section }}</h2>
            @if (currentPageData()!.sectionDescription) {
              <p class="section-description">{{ currentPageData()!.sectionDescription }}</p>
            }
          </div>
        }

        <div class="questions-group">
          @for (question of currentPageData()!.questions; track question.id) {
            <div class="question-item">
              <div class="question-header">
                <label
                  class="question-label"
                  [id]="'question-label-' + question.id"
                  [for]="question.id"
                >
                  <span class="question-number">{{
                    'questionnaire.question_prefix'
                      | transloco: { number: getQuestionNumber(question.id) }
                  }}</span>
                  {{ question.question }}
                  @if (question.required !== false) {
                    <span class="required-indicator">*</span>
                  } @else {
                    <span class="optional-indicator">{{
                      'questionnaire.optional_label' | transloco
                    }}</span>
                  }
                </label>
                @if (question.helpText) {
                  <button
                    type="button"
                    class="help-button"
                    (click)="toggleHelpText(question.id)"
                    [attr.aria-label]="
                      'questionnaire.toggle_help_aria' | transloco: { question: question.question }
                    "
                  >
                    <mat-icon>{{
                      isHelpTextVisible(question.id) ? 'help' : 'help_outline'
                    }}</mat-icon>
                  </button>
                }
              </div>

              @if (question.helpText && isHelpTextVisible(question.id)) {
                <div class="help-tooltip">
                  <app-huxley state="idle" [size]="28" />
                  <div class="help-bubble">
                    <div class="help-bubble-arrow"></div>
                    <p>{{ question.helpText }}</p>
                  </div>
                </div>
              }

              @let rec = recommendationViews().get(question.id);
              <div
                class="ai-answer-workspace"
                role="group"
                [attr.aria-labelledby]="'question-label-' + question.id"
                [id]="'answer-region-' + question.id"
                [class.ai-answer-workspace--suggested]="!!rec"
              >
                <div class="question-input">
                  @switch (question.type) {
                    @case ('text') {
                      <div class="field full-width">
                        <input
                          [id]="question.id"
                          [value]="getAnswer(question.id) || ''"
                          (input)="onTextChange(question.id, $event)"
                          [disabled]="disabled()"
                          [placeholder]="'questionnaire.answer_placeholder' | transloco"
                        />
                      </div>
                    }
                    @case ('textarea') {
                      <div class="field full-width">
                        <textarea
                          [id]="question.id"
                          [value]="getAnswer(question.id) || ''"
                          (input)="onTextChange(question.id, $event)"
                          [disabled]="disabled()"
                          rows="4"
                          [placeholder]="'questionnaire.answer_placeholder' | transloco"
                        ></textarea>
                      </div>
                    }
                    @case ('radio') {
                      <mat-radio-group
                        class="radio-group"
                        [attr.aria-labelledby]="'question-label-' + question.id"
                        [value]="singleSelectionValue(question.id)"
                      >
                        @for (option of displayOptions(question); track option.value) {
                          <mat-radio-button
                            [name]="question.id"
                            [value]="option.value"
                            [checked]="getAnswer(question.id) === option.value"
                            (change)="onSelectionChange(question.id, option.value)"
                            [disabled]="disabled()"
                          >
                            {{ option.label }}
                          </mat-radio-button>
                        }
                        @if (question.hasOther) {
                          <mat-radio-button
                            [name]="question.id"
                            value="other"
                            [checked]="isOtherChosen(question.id)"
                            (change)="onSelectionChange(question.id, 'other')"
                            [disabled]="disabled()"
                          >
                            {{
                              otherOptionLabel(question) ||
                                ('questionnaire.other_label' | transloco)
                            }}
                          </mat-radio-button>
                          @if (isOtherChosen(question.id)) {
                            <span class="other-field-label"
                              >{{ 'questionnaire.please_specify' | transloco
                              }}<span class="required-indicator">*</span></span
                            >
                            <div class="field other-input">
                              <input
                                [value]="getOtherValue(question.id)"
                                [attr.aria-label]="
                                  ('questionnaire.please_specify' | transloco) +
                                  ': ' +
                                  question.question
                                "
                                (input)="onOtherTextChange(question.id, $event)"
                                [disabled]="disabled()"
                              />
                            </div>
                          }
                        }
                      </mat-radio-group>
                    }
                    @case ('checkbox') {
                      <div class="checkbox-group">
                        @for (option of displayOptions(question); track option.value) {
                          <mat-checkbox
                            [checked]="isChecked(question.id, option.value)"
                            (change)="onCheckboxChange(question.id, option.value, $event.checked)"
                            [disabled]="disabled()"
                          >
                            {{ option.label }}
                          </mat-checkbox>
                        }
                        @if (question.hasOther) {
                          <mat-checkbox
                            [checked]="isChecked(question.id, 'other')"
                            (change)="onCheckboxChange(question.id, 'other', $event.checked)"
                            [disabled]="disabled()"
                          >
                            {{
                              otherOptionLabel(question) ||
                                ('questionnaire.other_label' | transloco)
                            }}
                          </mat-checkbox>
                          @if (isChecked(question.id, 'other')) {
                            <span class="other-field-label"
                              >{{ 'questionnaire.please_specify' | transloco
                              }}<span class="required-indicator">*</span></span
                            >
                            <div class="field other-input">
                              <input
                                [value]="getOtherValue(question.id)"
                                [attr.aria-label]="
                                  ('questionnaire.please_specify' | transloco) +
                                  ': ' +
                                  question.question
                                "
                                (input)="onOtherTextChange(question.id, $event)"
                                [disabled]="disabled()"
                              />
                            </div>
                          }
                        }
                      </div>
                    }
                    @case ('dropdown') {
                      <app-select
                        variant="form"
                        [inputId]="question.id"
                        [ariaLabel]="question.question"
                        [placeholder]="'questionnaire.select_option' | transloco"
                        [options]="dropdownOptions().get(question.id) ?? []"
                        [value]="singleSelectionValue(question.id)"
                        (valueChange)="onDropdownChange(question.id, $event)"
                        [clearable]="false"
                        [disabled]="disabled()"
                      />
                      @if (question.hasOther && isOtherChosen(question.id)) {
                        <span class="other-field-label"
                          >{{ 'questionnaire.please_specify' | transloco
                          }}<span class="required-indicator">*</span></span
                        >
                        <div class="field other-input">
                          <input
                            [value]="getOtherValue(question.id)"
                            [attr.aria-label]="
                              ('questionnaire.please_specify' | transloco) +
                              ': ' +
                              question.question
                            "
                            (input)="onOtherTextChange(question.id, $event)"
                            [disabled]="disabled()"
                          />
                        </div>
                      }
                    }
                  }
                </div>

                <!-- AI Recommendation Card. Everything rendered here is
                   baked in `recommendationViews` — one map lookup per
                   question, no per-cycle re-resolution of option labels. -->
                @if (rec) {
                  <div
                    class="ai-suggestion-card ai-suggestion-card--integrated"
                    [attr.aria-busy]="rec.state === 'loading'"
                    [id]="'recommendation-' + question.id"
                    [class.ai-suggestion-card--tentative]="rec.isTentative"
                    [class.ai-suggestion-card--updating]="rec.state === 'loading'"
                  >
                    <div class="ai-suggestion-header">
                      <div class="ai-suggestion-title">
                        <app-action-glyph name="synthesis" class="ai-suggestion-icon" />
                        <span>{{
                          (rec.isTentative
                            ? 'questionnaire.recommendation_title_tentative'
                            : 'questionnaire.recommendation_title'
                          ) | transloco
                        }}</span>
                      </div>
                      @if (canRegenerateSuggestions()) {
                        <app-button
                          type="primary"
                          variant="ghost"
                          size="small"
                          [iconOnly]="true"
                          #recommendationRefresh
                          [ariaControls]="'recommendation-' + question.id"
                          [disabled]="disabled() || isRecommendationBusy()"
                          [ariaLabel]="
                            ('questionnaire.recommendation_resuggest' | transloco) +
                            ': ' +
                            question.question
                          "
                          [tooltip]="'questionnaire.recommendation_resuggest' | transloco"
                          (clicked)="requestRegenerateQuestion(question.id)"
                        >
                          <app-icon iconKey="refresh" [showLabel]="false" />
                        </app-button>
                      }
                    </div>
                    @if (rec.state === 'loading') {
                      <app-ai-generating-status appearance="inline">{{
                        'questionnaire.recommendation_regenerating' | transloco
                      }}</app-ai-generating-status>
                    }
                    @if (
                      rec.state === 'suggestion' || (rec.state === 'loading' && rec.displayValue)
                    ) {
                      <div
                        class="ai-suggestion-value"
                        [attr.aria-hidden]="rec.state === 'loading' ? true : null"
                      >
                        {{ rec.displayValue }}
                      </div>
                      @if (rec.reasoning) {
                        <div class="ai-suggestion-reasoning">{{ rec.reasoning }}</div>
                      }
                      @if (rec.isTentative) {
                        <!-- Keep uncertainty visible alongside the answer, before the action. -->
                        <p class="ai-suggestion-note">
                          {{ 'questionnaire.recommendation_tentative_note' | transloco }}
                        </p>
                      }
                    } @else if (rec.state === 'applied') {
                      <p class="ai-suggestion-reasoning" role="status">
                        {{ 'questionnaire.recommendation_applied' | transloco }}
                      </p>
                    } @else if (rec.state === 'dismissed') {
                      <p class="ai-suggestion-reasoning" role="status">
                        {{ 'questionnaire.recommendation_dismissed' | transloco }}
                      </p>
                    } @else if (rec.state !== 'loading') {
                      <div class="ai-suggestion-value ai-suggestion-value--empty">
                        {{ 'questionnaire.recommendation_no_suggestion' | transloco }}
                      </div>
                      @if (rec.reasoning) {
                        <div class="ai-suggestion-reasoning">{{ rec.reasoning }}</div>
                      }
                    }
                    <span class="ai-suggestion-actions">
                      @if (
                        rec.state === 'suggestion' || (rec.state === 'loading' && rec.displayValue)
                      ) {
                        <app-change-decision
                          [name]="question.question"
                          [disabled]="disabled() || isRecommendationBusy()"
                          [rejectDisabled]="
                            !canRegenerateSuggestions() || isSuggestionsGenerating()
                          "
                          [busy]="dismissingQuestionId() === question.id ? 'reject' : null"
                          [rejectTooltip]="'questionnaire.recommendation_dismiss' | transloco"
                          [rejectLabel]="
                            'questionnaire.recommendation_dismiss_aria'
                              | transloco: { question: question.question }
                          "
                          (accepted)="applyRecommendation(question.id)"
                          (rejected)="requestDismissRecommendation(question.id)"
                        />
                      }
                    </span>
                  </div>
                }
              </div>
            </div>
          }
        </div>
      </div>
    </div>

    <!-- Pagination Footer -->
    <div class="pagination-container">
      <div class="pagination-inner">
        <div class="pagination-progress">
          <div class="progress-track">
            <div class="progress-fill" [style.width.%]="progressPercentage()"></div>
          </div>
        </div>
        @if (isInEditMode() && !shouldShowReviewButton()) {
          <button
            class="pagination-button pagination-close"
            type="button"
            [disabled]="disabled()"
            (click)="closeRequested.emit()"
            [attr.aria-label]="'questionnaire.close_aria' | transloco"
          >
            <mat-icon>close</mat-icon>
            <span>{{ 'questionnaire.close' | transloco }}</span>
          </button>
        }
        <div class="pagination-controls">
          <button
            class="pagination-button"
            [disabled]="!canGoPrevious() || disabled()"
            (click)="goToPrevious()"
            [attr.aria-label]="'questionnaire.previous_page_aria' | transloco"
            type="button"
          >
            <mat-icon>chevron_left</mat-icon>
            <span>{{ 'questionnaire.previous' | transloco }}</span>
          </button>

          <span class="pagination-label">{{ progressLabel() }}</span>

          <button
            class="pagination-button"
            [disabled]="!canGoNext() || disabled()"
            (click)="goToNext()"
            [attr.aria-label]="
              shouldShowReviewButton()
                ? ('questionnaire.go_to_review_aria' | transloco)
                : ('questionnaire.next_page_aria' | transloco)
            "
            [class.primary]="shouldShowReviewButton()"
            [title]="
              !isCurrentPageComplete() ? ('questionnaire.answer_all_tooltip' | transloco) : ''
            "
            type="button"
          >
            <span>{{
              (shouldShowReviewButton() ? 'questionnaire.review' : 'questionnaire.next') | transloco
            }}</span>
            <mat-icon>chevron_right</mat-icon>
          </button>
        </div>
      </div>
    </div>
  }
</div>
